import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ExchangeModuleRoute } from "@/components/exchange/ExchangeModuleRoute";
import { AdminAuthProvider } from "@/hooks/useAdminAuth";
import AdminNextRedirect from "@/components/admin/AdminNextRedirect";
import { PartnerAuthProvider } from "@/hooks/usePartnerAuth";
import { PartnerContextProvider } from "@/hooks/usePartnerContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import { VisitorTrackingProvider } from "@/contexts/VisitorTrackingContext";
import { CookieConsentProvider } from "@/hooks/useCookieConsent";

import { ResourcePreviewProvider } from "@/contexts/ResourcePreviewContext";
import { RedirectWithCanonical } from "@/components/RedirectWithCanonical";
import { TAG_LANDING_PATHS } from "@/lib/tagLinks";


// Eagerly load the homepage for fastest initial render
import Index from "./pages/Index";
import { CMSOverlayPage } from "@/components/CMSOverlayPage";

// Lazy-load all other pages to reduce unused JS on initial load
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const LeadDetailPage = lazy(() => import("./pages/admin/LeadDetailPage"));
const Solutions = lazy(() => import("./pages/Solutions"));
const Resources = lazy(() => import("./pages/Resources"));
const ResourceSlugRoute = lazy(() => import("./pages/ResourceSlugRoute"));
const Products = lazy(() => import("./pages/Products"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ShortLink = lazy(() => import("./pages/ShortLink"));
const CustomRouteResolver = lazy(() => import("./components/CustomRouteResolver"));

// Solution pages
const MobileUC = lazy(() => import("./pages/solutions/MobileUC"));
const Recording = lazy(() => import("./pages/solutions/Recording"));
const DistributedWorkforce = lazy(() => import("./pages/solutions/DistributedWorkforce"));
const RemoteWorker = lazy(() => import("./pages/solutions/RemoteWorker"));
const MobileWorkforce = lazy(() => import("./pages/solutions/MobileWorkforce"));
const TextMessaging = lazy(() => import("./pages/solutions/TextMessaging"));
const Frontline = lazy(() => import("./pages/solutions/Frontline"));

// Product pages
const Teams = lazy(() => import("./pages/products/Teams"));
const Webex = lazy(() => import("./pages/products/Webex"));
const UCPlatforms = lazy(() => import("./pages/products/UCPlatforms"));
const ESIM = lazy(() => import("./pages/products/ESIM"));

// Utility pages
const ValueCalculator = lazy(() => import("./pages/ValueCalculator"));
const CountryAvailability = lazy(() => import("./pages/CountryAvailability"));
const FrostSullivanAward = lazy(() => import("./pages/FrostSullivanAward"));
const Blog = lazy(() => import("./pages/Blog"));
const News = lazy(() => import("./pages/News"));
const Subscribe = lazy(() => import("./pages/Subscribe"));
const Partners = lazy(() => import("./pages/Partners"));
const GlobalService = lazy(() => import("./pages/GlobalService"));
const WhereToBuy = lazy(() => import("./pages/WhereToBuy"));
const SupportedPhones = lazy(() => import("./pages/SupportedPhones"));
const Plans = lazy(() => import("./pages/Plans"));
// ApprovedPartners and HowToBuy now redirect to /where-to-buy

// Dynamic content pages
const BlogPost = lazy(() => import("./pages/BlogPost"));
const DynamicBlogPost = lazy(() => import("./pages/DynamicBlogPost"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const CategoryArchive = lazy(() => import("./pages/CategoryArchive"));
const Terms = lazy(() => import("./pages/Terms"));
const Cookies = lazy(() => import("./pages/Cookies"));
const PillarPage = lazy(() => import("./pages/PillarPage"));
const ClusterPage = lazy(() => import("./pages/ClusterPage"));
const Industries = lazy(() => import("./pages/Industries"));
const SolutionsIndex = lazy(() => import("./pages/SolutionsIndex"));

// New pages
const FAQs = lazy(() => import("./pages/FAQs"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Privacy = lazy(() => import("./pages/Privacy"));
const AcceptableUsePolicy = lazy(() => import("./pages/AcceptableUsePolicy"));
const FixedMobileConvergence = lazy(() => import("./pages/FixedMobileConvergence"));
const PersonalPhones = lazy(() => import("./pages/PersonalPhones"));
const CompanyPhones = lazy(() => import("./pages/CompanyPhones"));
const LeadershipTeam = lazy(() => import("./pages/LeadershipTeam"));
const PartnerDirectory = lazy(() => import("./pages/PartnerDirectory"));
const PartnerProfile = lazy(() => import("./pages/PartnerProfile"));
const Finance = lazy(() => import("./pages/Finance"));
const PortaOne = lazy(() => import("./pages/PortaOne"));
const GlobalESIM = lazy(() => import("./pages/GlobalESIM"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const StylePreview = lazy(() => import("./pages/StylePreview"));
const TwentyYears = lazy(() => import("./pages/TwentyYears"));
const CampaignPage = lazy(() => import("./pages/CampaignPage"));
const BriefViewer = lazy(() => import("./pages/BriefViewer"));
const ExecutiveBriefing = lazy(() => import("./pages/ExecutiveBriefing"));
const BetterBusiness = lazy(() => import("./pages/BetterBusiness"));
const Research = lazy(() => import("./pages/Research"));
const TagLanding = lazy(() => import("./pages/TagLanding"));

const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const EmailPreferences = lazy(() => import("./pages/EmailPreferences"));
const EmbedCountryAvailability = lazy(() => import("./pages/EmbedCountryAvailability"));
const EmbedCoverageMap = lazy(() => import("./pages/EmbedCoverageMap"));
const EmbedSupportedPhones = lazy(() => import("./pages/EmbedSupportedPhones"));
const EmbedValueCalculator = lazy(() => import("./pages/EmbedValueCalculator"));

// Tango Exchange (Partner Portal)
const ExchangeLogin = lazy(() => import("./pages/exchange/ExchangeLogin"));
const ExchangeHome = lazy(() => import("./pages/exchange/ExchangeHome"));
const ExchangeOverview = lazy(() => import("./pages/exchange/ExchangeOverview"));
const ExchangeProfile = lazy(() => import("./pages/exchange/ExchangeProfile"));
const ExchangeAssets = lazy(() => import("./pages/exchange/ExchangeAssets"));
const ExchangeDeals = lazy(() => import("./pages/exchange/ExchangeDeals"));
const ExchangeTraining = lazy(() => import("./pages/exchange/ExchangeTraining"));
const ExchangeMDF = lazy(() => import("./pages/exchange/ExchangeMDF"));
const ExchangeAcceptInvite = lazy(() => import("./pages/exchange/ExchangeAcceptInvite"));
const CommsAdmin = lazy(() => import("./pages/exchange/admin/CommsAdmin"));
const SignatureGenerator = lazy(() => import("./pages/admin/SignatureGenerator"));
const PublicSignatureGenerator = lazy(() => import("./pages/PublicSignatureGenerator"));
const ExchangeRooms = lazy(() => import("./pages/exchange/ExchangeRooms"));
const ExchangeRoom = lazy(() => import("./pages/exchange/ExchangeRoom"));
const ExchangeRoomEdit = lazy(() => import("./pages/exchange/ExchangeRoomEdit"));
const PublicDataRoom = lazy(() => import("./pages/PublicDataRoom"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <CookieConsentProvider>
        <VisitorTrackingProvider>

        <AdminAuthProvider>
        <AdminNextRedirect />
        <PartnerAuthProvider>
        <PartnerContextProvider>
        <ResourcePreviewProvider>
        <Suspense fallback={null}>
        <Routes>
          {/* Tracked LinkedIn short link — bare forwarder, no tracking hooks */}
          <Route path="/l/:code" element={<ShortLink />} />

          {/* Embed routes (no Layout wrapper) */}
          <Route path="/embed/country-availability" element={<EmbedCountryAvailability />} />
          <Route path="/embed/coverage-map" element={<EmbedCoverageMap />} />
          <Route path="/embed/supported-phones" element={<EmbedSupportedPhones />} />
          <Route path="/embed/value-calculator" element={<EmbedValueCalculator />} />

          {/* Main pages */}
          <Route path="/" element={<Index />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/resources" element={<Resources />} />
          {/* Resolves html-kind assets to a real document page; anything else
              falls through to the library index inside the same component. */}
          <Route path="/resources/:slug" element={<ResourceSlugRoute />} />
          <Route path="/research" element={<Research />} />
          <Route path="/tango-extend" element={<Products />} />
          <Route path="/products" element={<Products />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          
          {/* Solution detail pages */}
          <Route path="/solutions/mobile-uc" element={<MobileUC />} />
          <Route path="/solutions/recording" element={<Recording />} />
          <Route path="/solutions/distributed-workforce" element={<DistributedWorkforce />} />
          <Route path="/solutions/remote-worker" element={<RemoteWorker />} />
          <Route path="/solutions/mobile-workforce" element={<MobileWorkforce />} />
          <Route path="/solutions/enterprise-text-messaging" element={<CMSOverlayPage slug="solution-text-messaging"><TextMessaging /></CMSOverlayPage>} />
          <Route path="/solutions/frontline" element={<Frontline />} />
          
          {/* Original URL aliases for solutions */}
          <Route path="/solutions/hybrid-and-frontline-workforce" element={<Frontline />} />
          <Route path="/solutions/mobile-call-and-text-recording" element={<Recording />} />
          <Route path="/solutions/mobile-workforce-communications" element={<MobileWorkforce />} />
          <Route path="/solutions/work-from-anywhere-remote-worker-teleworker-communications" element={<RemoteWorker />} />
          <Route path="/solutions/mobile-first-communications-for-the-distributed-hybrid-workforce" element={<DistributedWorkforce />} />
          <Route path="/solutions/cisco-collaboration-sms-texting-solutions" element={<RedirectWithCanonical to="/solutions/enterprise-text-messaging" title="Enterprise Text Messaging" />} />
          <Route path="/mobile-unified-communications" element={<MobileUC />} />
          
          {/* Product detail pages */}
          <Route path="/products/teams" element={<Teams />} />
          <Route path="/products/tango-extend" element={<RedirectWithCanonical to="/tango-extend" title="Tango Extend Products" />} />
          <Route path="/products/webex" element={<Webex />} />
          <Route path="/products/uc-platforms" element={<UCPlatforms />} />
          <Route path="/products/esim" element={<ESIM />} />
          <Route path="/products/mobile-uc" element={<MobileUC />} />
          <Route path="/supported-phones" element={<CMSOverlayPage slug="supported-phones"><SupportedPhones /></CMSOverlayPage>} />
          
          {/* Original URL aliases for products */}
          <Route path="/tango-extend-for-microsoft-teams" element={<Teams />} />
          <Route path="/tango-extend-for-cisco-webex" element={<Webex />} />
          <Route path="/uc-platforms" element={<UCPlatforms />} />
          <Route path="/esim-activation-tango-extend" element={<ESIM />} />
          <Route path="/fixed-mobile-convergence" element={<FixedMobileConvergence />} />
          <Route path="/fixed-mobile-convergence-tango-extend" element={<FixedMobileConvergence />} />
          <Route path="/tango-extend-for-portaone" element={<PortaOne />} />
          <Route path="/global-esim" element={<GlobalESIM />} />
          <Route path="/tango-extend-global-esim" element={<GlobalESIM />} />
          
          {/* Utility pages */}
          <Route path="/value-calculator" element={<CMSOverlayPage slug="value-calculator"><ValueCalculator /></CMSOverlayPage>} />
          <Route path="/country-availability" element={<CMSOverlayPage slug="country-availability"><CountryAvailability /></CMSOverlayPage>} />
          <Route path="/frost-sullivan-award" element={<CMSOverlayPage slug="frost-sullivan-award"><FrostSullivanAward /></CMSOverlayPage>} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/news" element={<News />} />
          <Route path="/subscribe" element={<Subscribe />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/channel-partners" element={<Partners />} />
          <Route path="/partners/:slug" element={<PartnerProfile />} />
          <Route path="/where-to-buy" element={<CMSOverlayPage slug="where-to-buy"><WhereToBuy /></CMSOverlayPage>} />
          <Route path="/global-service" element={<GlobalService />} />
          <Route path="/plans" element={<CMSOverlayPage slug="plans"><Plans /></CMSOverlayPage>} />
          <Route path="/approved-partners-for-microsoft-teams-and-webex-calling" element={<RedirectWithCanonical to="/where-to-buy?accredited=true" title="Approved Partners" />} />
          <Route path="/how-to-buy" element={<RedirectWithCanonical to="/where-to-buy" title="How to Buy" />} />
          <Route path="/about-tango-networks" element={<RedirectWithCanonical to="/about" title="About Tango Networks" />} />

          {/* Legacy WordPress URL redirects (consolidate meta signals to current pages) */}
          <Route path="/tango-extend-for-teams-and-webex" element={<RedirectWithCanonical to="/products/teams" title="Tango Extend for Teams and Webex" />} />
          <Route path="/audiocodes-mobile-connect-powered-by-tango-networks" element={<RedirectWithCanonical to="/products/uc-platforms" title="AudioCodes Mobile Connect" />} />
          <Route path="/private-lte" element={<RedirectWithCanonical to="/products/esim" title="Private LTE" />} />
          <Route path="/global-availability-commsverse" element={<RedirectWithCanonical to="/country-availability" title="Global Availability" />} />
          <Route path="/terms-of-service" element={<RedirectWithCanonical to="/terms" title="Terms of Service" />} />
          <Route path="/solutions/mobile-workforce-communications" element={<RedirectWithCanonical to="/solutions/mobile-workforce" title="Mobile Workforce Communications" />} />
          <Route path="/solutions/work-from-anywhere-remote-worker-teleworker-communications" element={<RedirectWithCanonical to="/solutions/remote-worker" title="Remote Worker Communications" />} />
          <Route path="/solutions/text-messaging" element={<RedirectWithCanonical to="/solutions/enterprise-text-messaging" title="Enterprise Text Messaging" />} />

          {/* Legacy top-level blog slugs → /blog/<slug> */}
          <Route path="/empowering-mobile-workers-white-paper-outlines-communications-innovations-for-the-frontline" element={<RedirectWithCanonical to="/blog/empowering-mobile-workers-white-paper-outlines-communications-innovations-for-the-frontline" />} />
          <Route path="/tango-networks-partners-with-bt-wholesale-to-deliver-best-in-class-mvno-business-mobile-services-in-the-uk" element={<RedirectWithCanonical to="/blog/tango-networks-partners-with-bt-wholesale-to-deliver-best-in-class-mvno-business-mobile-services-in-the-uk" />} />
          <Route path="/tango-extend-achieves-two-key-milestones-as-mobile-unified-communications-becomes-must-have-business-solution" element={<RedirectWithCanonical to="/blog/tango-extend-achieves-two-key-milestones-as-mobile-unified-communications-becomes-must-have-business-solution" />} />
          <Route path="/tango-extend-recognized-in-mvno-of-the-year-awards-by-mobile-europe" element={<RedirectWithCanonical to="/blog/tango-extend-recognized-in-mvno-of-the-year-awards-by-mobile-europe" />} />
          <Route path="/business-without-borders-the-simple-single-mobile-subscription-for-the-international-workforce" element={<RedirectWithCanonical to="/blog/business-without-borders-the-simple-single-mobile-subscription-for-the-international-workforce" />} />
          <Route path="/bringing-mobile-unified-communications-to-the-deskless-frontline-workforce" element={<RedirectWithCanonical to="/blog/bringing-mobile-unified-communications-to-the-deskless-frontline-workforce" />} />
          <Route path="/tango-introduces-2nd-sim-use-for-business" element={<RedirectWithCanonical to="/blog/tango-introduces-2nd-sim-use-for-business" />} />
          <Route path="/universal-uc-endpoint-era-how-tango-extend-supports-all-workers" element={<RedirectWithCanonical to="/blog/universal-uc-endpoint-era-how-tango-extend-supports-all-workers" />} />
          <Route path="/the-cheap-data-sim-that-actually-costs-a-fortune-in-pots-replacement" element={<RedirectWithCanonical to="/blog/the-cheap-data-sim-that-actually-costs-a-fortune-in-pots-replacement" />} />
          <Route path="/achieving-control-compliance-avoidable-fines-continue-to-mount-for-mobile-comms-recording-violations" element={<RedirectWithCanonical to="/blog/achieving-control-compliance-avoidable-fines-continue-to-mount-for-mobile-comms-recording-violations" />} />
          
          {/* New pages */}
          <Route path="/20-years" element={<CMSOverlayPage slug="twenty-years"><TwentyYears /></CMSOverlayPage>} />
          <Route path="/better-business" element={<BetterBusiness />} />
          
          <Route path="/faqs" element={<FAQs />} />
          <Route path="/resources/faqs" element={<FAQs />} />
          <Route path="/how-it-works" element={<CMSOverlayPage slug="how-it-works"><HowItWorks /></CMSOverlayPage>} />
          <Route path="/privacy" element={<CMSOverlayPage slug="privacy"><Privacy /></CMSOverlayPage>} />
          <Route path="/acceptable-use-policy" element={<CMSOverlayPage slug="acceptable-use-policy"><AcceptableUsePolicy /></CMSOverlayPage>} />
          <Route path="/sims-for-personal-byod-mobile-phones" element={<CMSOverlayPage slug="sims-personal"><PersonalPhones /></CMSOverlayPage>} />
          <Route path="/sims-for-company-owned-mobile-phones" element={<CMSOverlayPage slug="sims-company"><CompanyPhones /></CMSOverlayPage>} />
          <Route path="/about-tango-networks/leadership-team" element={<CMSOverlayPage slug="leadership-team"><LeadershipTeam /></CMSOverlayPage>} />
          <Route path="/partner-directory" element={<CMSOverlayPage slug="partner-directory"><PartnerDirectory /></CMSOverlayPage>} />
          <Route path="/finance" element={<CMSOverlayPage slug="finance"><Finance /></CMSOverlayPage>} />
          <Route path="/uk-coverage" element={<RedirectWithCanonical to="/country-availability" title="UK Coverage" />} />
          
          {/* Dynamic content pages */}
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/news/:slug" element={<NewsArticle />} />
          <Route path="/category/:slug" element={<CategoryArchive />} />

          {/* Tag landing pages — surface blogs, news and resources for a tag */}
          {TAG_LANDING_PATHS.map((p) => (
            <Route key={p} path={`/${p}/:slug`} element={<TagLanding />} />
          ))}


          {/* Legacy pillar slug redirects (emit canonical + noindex for crawlers) */}
          <Route path="/solutions/p/global-availability" element={<RedirectWithCanonical to="/solutions/p/work-anywhere" />} />
          <Route path="/solutions/p/control-compliance" element={<RedirectWithCanonical to="/solutions/p/control" />} />
          <Route path="/solutions/p/cost-sustainability" element={<RedirectWithCanonical to="/solutions/p/one-voice" />} />
          <Route path="/solutions/p/engagement-experience" element={<RedirectWithCanonical to="/solutions/p/your-people" />} />
          <Route path="/solutions/p/stay-connected" element={<RedirectWithCanonical to="/solutions/p/always-on" />} />
          <Route path="/solutions/p/simplifying-global-working" element={<RedirectWithCanonical to="/solutions/p/work-anywhere" />} />
          <Route path="/solutions/p/cost" element={<RedirectWithCanonical to="/solutions/p/one-voice" />} />
          <Route path="/solutions/p/engagement" element={<RedirectWithCanonical to="/solutions/p/your-people" />} />
          <Route path="/solutions/p/experience" element={<RedirectWithCanonical to="/solutions/p/your-customers" />} />
          <Route path="/solutions/p/business-continuity" element={<RedirectWithCanonical to="/solutions/p/always-on" />} />
          <Route path="/solutions/p/simplify-global-working" element={<RedirectWithCanonical to="/solutions/p/work-anywhere" />} />
          <Route path="/solutions/p/pots-replacement" element={<RedirectWithCanonical to="/solutions/p/always-on" />} />
           {/* Solutions & Industries index pages */}
          <Route path="/solutions/all" element={<SolutionsIndex />} />
          <Route path="/solutions/industries" element={<Industries />} />
          {/* Dynamic pillar & cluster pages */}
          <Route path="/solutions/p/:slug" element={<PillarPage />} />
          <Route path="/solutions/c/:slug" element={<ClusterPage />} />

          {/* Legal pages */}
          <Route path="/terms" element={<CMSOverlayPage slug="terms"><Terms /></CMSOverlayPage>} />
          <Route path="/cookies" element={<CMSOverlayPage slug="cookies"><Cookies /></CMSOverlayPage>} />

          {/* Admin portal */}
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* OAuth consent for connected agents (MCP clients) */}
          <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/leads/:id" element={<LeadDetailPage />} />
          <Route path="/admin/tools/signature-generator" element={<SignatureGenerator />} />
          <Route path="/admin/tools/data-rooms" element={<Navigate to="/admin?tab=data_rooms" replace />} />
          <Route path="/tools/email-signature" element={<PublicSignatureGenerator />} />

          {/* Tango Exchange (Partner Portal) */}
          <Route path="/exchange/login" element={<ExchangeLogin />} />
          <Route path="/exchange/accept-invite/:token" element={<ExchangeAcceptInvite />} />
          <Route path="/exchange/overview" element={<ExchangeOverview />} />
          <Route path="/exchange/profile" element={<ExchangeProfile />} />
          <Route path="/exchange/assets" element={<ExchangeAssets />} />
          <Route path="/exchange/rooms" element={<ExchangeRooms />} />
          <Route path="/exchange/rooms/new" element={<ExchangeRoomEdit />} />
          <Route path="/exchange/rooms/:slug" element={<ExchangeRoom />} />
          <Route path="/exchange/rooms/:slug/edit" element={<ExchangeRoomEdit />} />
          <Route path="/exchange/deals" element={<ExchangeModuleRoute module="deals"><ExchangeDeals /></ExchangeModuleRoute>} />
          <Route path="/exchange/training" element={<ExchangeModuleRoute module="training"><ExchangeTraining /></ExchangeModuleRoute>} />
          <Route path="/exchange/mdf" element={<ExchangeModuleRoute module="mdf"><ExchangeMDF /></ExchangeModuleRoute>} />
          <Route path="/exchange" element={<ExchangeHome />} />
          <Route path="/exchange/admin/comms" element={<CommsAdmin />} />

          {/* Public data rooms (shareable with prospects) */}
          <Route path="/rooms/:slug" element={<PublicDataRoom />} />

          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/style-preview" element={<StylePreview />} />
          
          {/* Campaign landing pages */}
          <Route path="/campaign/:slug" element={<CampaignPage />} />

          {/* Executive brief viewer */}
          <Route path="/brief/:reportId" element={<BriefViewer />} />

          {/* Executive briefing (magic link) */}
          <Route path="/briefing/:token" element={<ExecutiveBriefing />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/email-preferences" element={<EmailPreferences />} />

          {/* Root-level blog post slugs (SEO parity with original tango-networks.com) */}
          <Route path="/:slug" element={<DynamicBlogPost />} />

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          {/* Resolution order: explicit routes above → root-level blog slug
              (DynamicBlogPost) → custom campaign URL (page_settings.route_path)
              → NotFound. Custom URLs can never shadow a real route. */}
          <Route path="*" element={<CustomRouteResolver />} />
        </Routes>
        </Suspense>
        </ResourcePreviewProvider>
        </PartnerContextProvider>
        </PartnerAuthProvider>
        </AdminAuthProvider>
        </VisitorTrackingProvider>
        </CookieConsentProvider>

      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;